import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import {
  ChevronLeft, Pencil, Phone, Mail, MessageSquare, Send, X, Save, Plus,
  FileText, Download, Trash2, Upload, MoreHorizontal, Check, FileVideo,
  FileSpreadsheet, IdCard, ScrollText, FolderOpen, ChevronRight,
  Activity as ActivityIcon, Briefcase, Info as InfoIcon, FileType2,
  Users, ShieldCheck, ArrowUpRight, ChevronDown,
  Calendar as CalIcon, Route as RouteIcon, Sparkles, StickyNote,
} from "lucide-react";
import NotesTab from "@/components/player/NotesTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { STATUSES, STATUS_BADGE, formatDate, relativeFr, cn } from "@/lib/utils";
import { toast } from "sonner";
import RelanceButton, { useRelance } from "@/components/RelanceButton";
import AvatarUpload from "@/components/player/AvatarUpload";
import AthleteInfoTab from "@/components/player/AthleteInfoTab";
import { useLang, translateFr as tfr } from "@/i18n";
import { quotaMessage } from "@/lib/quotaMessages";

const DOC_TYPE_META = {
  cv: { label: "CV sportif", Icon: FileText, color: "text-danger bg-danger/10" },
  highlights: { label: "Highlights", Icon: FileVideo, color: "text-info bg-info/10" },
  stats: { label: "Statistiques", Icon: FileSpreadsheet, color: "text-warning bg-warning/10" },
  id: { label: tfr("Pièce d'identité"), Icon: IdCard, color: "text-info bg-info/10" },
  contract: { label: tfr("Contrat"), Icon: ScrollText, color: "text-success bg-success/10" },
  scout: { label: "Rapport scout", Icon: FileText, color: "text-foreground bg-secondary dark:bg-secondary/50" },
  negotiation: { label: tfr("Fiche de négociation"), Icon: FileText, color: "text-[#6D3EEA] dark:text-[#A78BFA] bg-[#6D3EEA]/15" },
  career_plan: { label: tfr("Plan de carrière"), Icon: FileText, color: "text-athlynx bg-athlynx/10" },
  other: { label: "Autre", Icon: FileText, color: "text-muted-foreground bg-secondary dark:bg-secondary/50" },
};


export default function PlayerProfile() {
  const { t: tr } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [tab, setTab] = useState(() =>
    new URLSearchParams(window.location.search).get("tab") === "notes" ? "notes" : "resume");

  const toggleEdit = () => {
    if (editMode) { setEditMode(false); return; }
    setTab("info");
    setEditMode(true);
  };

  const changeTab = (v) => {
    setTab(v);
    if (editMode && v !== "info") setEditMode(false);
  };

  const load = useCallback(async () => {
    try {
      const [r, d] = await Promise.all([
        api.get(`/players/${id}`),
        api.get(`/players/${id}/documents`),
      ]);
      setData(r.data);
      setDocs(d.data || []);
    } catch {
      toast.error(tfr("Joueur introuvable"));
      navigate("/pipeline");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => {
    setData(null); setLoading(true); setEditMode(false);
    setTab(new URLSearchParams(window.location.search).get("tab") === "notes" ? "notes" : "resume");
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return <div className="p-8 text-sm text-muted-foreground">{tr("common.loading")}</div>;
  }
  const player = data.player;

  return (
    <div className="pb-32 md:pb-8" data-testid="player-profile">
      {/* === HEADER === */}
      <ProfileHeader
        player={player}
        editMode={editMode}
        onBack={() => navigate(-1)}
        onToggleEdit={toggleEdit}
        onUpdated={load}
      />

      {/* === TABS === */}
      <div className="px-4 md:px-8 max-w-5xl mx-auto">
        <Tabs value={tab} onValueChange={changeTab} className="mt-1.5 md:mt-3">
          <TabsList className="tabstrip-x bg-transparent border-b border-border dark:border-border dark:border-border rounded-none w-full justify-start h-auto p-0 gap-0">
            {[
              { v: "resume", l: tr("athlete.tab.resume"), I: FileText },
              { v: "info", l: tr("athlete.tab.info"), I: InfoIcon },
              { v: "career", l: tr("athlete.tab.career"), I: Briefcase },
              { v: "docs", l: tr("athlete.tab.docs"), I: FolderOpen },
              { v: "notes", l: tr("athlete.tab.notes"), I: StickyNote },
              { v: "activity", l: tr("athlete.tab.activity"), I: ActivityIcon },
            ].map((t) => {
              const Icon = t.I;
              return (
                <TabsTrigger
                  key={t.v}
                  value={t.v}
                  className="flex-none rounded-none -mb-px border-b-2 border-transparent data-[state=active]:border-info data-[state=active]:text-info data-[state=active]:shadow-none data-[state=active]:bg-transparent text-muted-foreground px-2.5 md:px-5 py-2 md:py-2.5 text-xs md:text-sm flex flex-row items-center gap-1.5 md:gap-2 transition-colors whitespace-nowrap ring-offset-0 focus-visible:ring-1 focus-visible:ring-info/60 focus-visible:ring-offset-0 focus-visible:ring-inset"
                  data-testid={`tab-${t.v}`}
                >
                  <Icon className="h-3.5 w-3.5 md:h-4 md:w-4 shrink-0" />
                  <span>{t.l}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="resume" className="mt-6 space-y-4">
            <ResumeTab player={player} docs={docs} onUpdated={load} />
          </TabsContent>
          <TabsContent value="info" className="mt-6">
            <AthleteInfoTab player={player} editMode={editMode} setEditMode={setEditMode} onUpdated={load} />
          </TabsContent>
          <TabsContent value="career" className="mt-6">
            <CareerTab player={player} interactions={data.interactions} onUpdated={load} />
          </TabsContent>
          <TabsContent value="docs" className="mt-6">
            <DocumentsTab playerId={player.id} docs={docs} onChange={load} />
          </TabsContent>
          <TabsContent value="notes" className="mt-6">
            <NotesTab player={player} />
          </TabsContent>
          <TabsContent value="activity" className="mt-6">
            <ActivityTab interactions={data.interactions} reminders={data.reminders} />
          </TabsContent>
        </Tabs>
      </div>

      {/* === STICKY FOOTER ACTIONS === */}
      <FooterActions player={player} onLogged={load} />
    </div>
  );
}

/* ===== HEADER ===== */
function CareerPlanButton({ player }) {
  const navigate = useNavigate();
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => navigate(`/players/${player.id}/career-plan`)}
      aria-label={`${tfr("Ouvrir le plan de carrière de")} ${player.name}`}
      className="h-9 w-full md:w-auto gap-1.5 border-border dark:border-border hover:bg-athlynx/[0.06] hover:border-athlynx/40 hover:text-athlynx focus-visible:ring-2 focus-visible:ring-athlynx/40 transition-colors"
      data-testid="career-plan-button"
    >
      <RouteIcon className="h-3.5 w-3.5" aria-hidden="true" />
      {tfr("Plan de carrière")}
    </Button>
  );
}

function IntelligenceButton({ player }) {
  const navigate = useNavigate();
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => navigate(`/intelligence?athlete_id=${player.id}`)}
      aria-label={`Ouvrir Athlynx Intelligence pour ${player.name}`}
      className="h-9 w-full md:w-auto gap-1.5 border-border dark:border-border hover:bg-athlynx/[0.06] hover:border-athlynx/40 hover:text-athlynx focus-visible:ring-2 focus-visible:ring-athlynx/40 transition-colors"
      data-testid="intelligence-button"
    >
      <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="sm:hidden">Intelligence</span>
      <span className="hidden sm:inline">Athlynx Intelligence</span>
    </Button>
  );
}

function NegotiationProfileButton({ player }) {
  const navigate = useNavigate();
  const [preparing, setPreparing] = useState(false);

  const open = async () => {
    if (preparing) return;
    setPreparing(true);
    try {
      await api.get(`/players/${player.id}/negotiation-profile/readiness`);
      navigate(`/players/${player.id}/negotiation-profile`);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 404) toast.error(tfr("Le profil de l'athlète est introuvable."));
      else if (s === 401 || s === 403) toast.error(tfr("Vous n'avez pas accès à cet athlète."));
      else toast.error(tfr("Impossible de préparer la fiche de négociation."));
    } finally { setPreparing(false); }
  };

  return (
    <Button
      onClick={open}
      disabled={preparing}
      aria-busy={preparing}
      aria-label={`${tfr("Créer une fiche de négociation pour")} ${player.name}`}
      size="sm"
      className="h-9 w-full md:w-auto gap-1.5 text-white font-medium border border-[#7C4AFF]/50 bg-[#6D3EEA]/85 hover:bg-[#6D3EEA] active:translate-y-px focus-visible:ring-2 focus-visible:ring-[#7B46F5]/40 disabled:opacity-80 transition-colors"
      data-testid="negotiation-profile-button"
      data-loading={preparing || undefined}
    >
      {preparing ? (
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" aria-hidden="true" />
          {tfr("Préparation…")}
        </span>
      ) : (
        <>
          <FileText className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="sm:hidden">{tfr("Négociation")}</span>
          <span className="hidden sm:inline">{tfr("Fiche de négociation")}</span>
        </>
      )}
    </Button>
  );
}

function OverflowMenu({ player, editMode, onToggleEdit, onUpdated }) {
  const navigate = useNavigate();
  const { tf } = useLang();
  const { hasPhone, hasEmail, callPhone, sendEmail } = useRelance({ player, onLogged: onUpdated });
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="rounded-full p-2 hover:bg-accent dark:hover:bg-secondary/60 text-muted-foreground hover:text-foreground transition-colors shrink-0"
          data-testid="profile-overflow-menu"
          aria-label={tf("Plus d'actions")}
        >
          <MoreHorizontal className="h-5 w-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuItem onClick={onToggleEdit} data-testid="menu-edit-profile">
          {editMode ? <X className="h-4 w-4 mr-2" /> : <Pencil className="h-4 w-4 mr-2" />}
          {editMode ? tf("Annuler la modification") : tf("Modifier le profil")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {hasPhone && (
          <DropdownMenuItem onClick={callPhone} data-testid="menu-relance-call">
            <Phone className="h-4 w-4 mr-2" /> {tf("Relancer par téléphone")}
          </DropdownMenuItem>
        )}
        {hasEmail && (
          <DropdownMenuItem onClick={sendEmail} data-testid="menu-relance-email">
            <Mail className="h-4 w-4 mr-2" /> {tf("Relancer par email")}
          </DropdownMenuItem>
        )}
        {!hasPhone && !hasEmail && (
          <DropdownMenuItem disabled data-testid="menu-relance-none">
            <Phone className="h-4 w-4 mr-2" /> {tf("Aucun contact renseigné")}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        {player.status === "Placé" && (
          <DropdownMenuItem onClick={() => navigate(`/placements?athlete=${player.id}`)} data-testid="menu-view-placement">
            <Briefcase className="h-4 w-4 mr-2" /> {tf("Voir le placement")}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => navigate(`/contracts?new=1&player=${player.id}`)} data-testid="menu-create-contract">
          <ScrollText className="h-4 w-4 mr-2" /> {tf("Créer un contrat")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ProfileHeader({ player, editMode, onBack, onToggleEdit, onUpdated }) {
  const { tf } = useLang();
  const updateStatus = async (status) => {
    try {
      await api.patch(`/players/${player.id}/status`, { status });
      toast.success(`Statut: ${status}`);
      onUpdated?.();
    } catch { toast.error(tfr("Erreur")); }
  };

  const subtitle = useMemo(
    () => [player.position, player.club].filter(Boolean).join(" · "),
    [player.position, player.club]
  );
  const metaLine = useMemo(
    () => [
      player.age ? `${player.age} ans` : null,
      player.level || null,
    ].filter(Boolean).join(" • "),
    [player.age, player.level]
  );

  return (
    <div
      className="sticky top-16 z-20 bg-background/85 backdrop-blur-md supports-[backdrop-filter]:bg-background/60 border-b border-border dark:border-border"
      data-testid="profile-header"
    >
      <div className="px-4 md:px-8 max-w-5xl mx-auto pt-2 pb-2.5 md:pt-3 md:pb-3">
        {/* Row 1 — identity */}
        <div className="flex items-center gap-2 md:gap-3">
          <button
            onClick={onBack}
            className="rounded-full p-1.5 -ml-1.5 hover:bg-accent dark:hover:bg-secondary dark:bg-secondary/50 transition-colors text-muted-foreground hover:text-foreground shrink-0"
            data-testid="profile-back"
            aria-label={tf("Retour")}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <AvatarUpload player={player} onUpdated={onUpdated} size="sm" />
          <div className="flex-1 min-w-0">
            <h1 className="text-base md:text-2xl font-semibold tracking-tight leading-tight truncate" data-testid="profile-name">
              {player.name}
            </h1>
            <p className="text-xs md:text-sm text-muted-foreground truncate leading-snug">
              {subtitle || "—"}
            </p>
            {metaLine && (
              <p className="text-[11px] text-muted-foreground/70 truncate leading-snug" data-testid="profile-meta">
                {metaLine}
              </p>
            )}
          </div>
          <OverflowMenu player={player} editMode={editMode} onToggleEdit={onToggleEdit} onUpdated={onUpdated} />
        </div>

        {/* Row 2 — context */}
        <div className="flex items-center gap-2.5 mt-2">
          <Select value={player.status} onValueChange={updateStatus}>
            <SelectTrigger
              className={cn("h-7 px-2.5 text-[11px] border w-auto gap-1", STATUS_BADGE[player.status])}
              data-testid="profile-status-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{tfr(s)}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-[11px] text-muted-foreground truncate" data-testid="profile-last-interaction">
            {tfr("Dernière interaction")} · {player.last_interaction_at ? relativeFr(player.last_interaction_at) : tfr("jamais")}
          </span>
        </div>

        {/* Row 3 — primary actions */}
        <div className="grid grid-cols-2 md:flex gap-2 mt-2 md:max-w-md">
          <NegotiationProfileButton player={player} />
          <CareerPlanButton player={player} />
          <IntelligenceButton player={player} />
        </div>
      </div>
    </div>
  );
}

/* ===== RESUME TAB ===== */
function ResumeTab({ player, docs, onUpdated }) {
  const { tf } = useLang();
  return (
    <div className="space-y-4">
      <Card title={tf("Informations clés")} testid="card-key-info">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-4">
          <Field label={tf("Nationalité")} value={player.nationality} />
          <Field label={tf("Taille")} value={player.height_cm ? `${(player.height_cm / 100).toFixed(2)} m` : null} />
          {player.foot ? <Field label={tf("Pied fort")} value={tf(player.foot)} /> : null}
          <Field label={tf("Poste principal")} value={player.position ? tf(player.position) : null} />
          <Field label={tf("Postes secondaires")} value={player.secondary_positions} />
          <Field label={tf("Niveau actuel")} value={player.level} />
        </div>
      </Card>
      {player.about && (
        <Card title={tf("À propos")} testid="card-about">
          <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-line">{player.about}</p>
        </Card>
      )}
      <DocumentsPreview playerId={player.id} docs={docs} onChange={onUpdated} />
      <TagsSection player={player} onUpdated={onUpdated} />
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70">{label}</div>
      <div className={cn("text-sm font-medium mt-1", !value && "text-muted-foreground/50")}>
        {value || "—"}
      </div>
    </div>
  );
}

function Card({ title, action, children, testid }) {
  return (
    <section
      className="rounded-3xl border border-border dark:border-border bg-card dark:bg-surface p-5"
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="text-[11px] uppercase tracking-[0.18em] font-semibold text-foreground/85">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function DocumentsPreview({ playerId, docs, onChange }) {
  const visible = docs.slice(0, 4);
  return (
    <Card
      title="Documents"
      testid="card-documents-preview"
      action={
        <Link
          to={`?tab=docs#docs`}
          onClick={(e) => {
            e.preventDefault();
            const trigger = document.querySelector('[data-testid=tab-docs]');
            trigger?.click();
          }}
          className="text-[11px] text-info hover:text-info/80 flex items-center gap-1"
          data-testid="docs-see-all"
        >
          {tfr("+ Ajouter un document")}
        </Link>
      }
    >
      {docs.length === 0 ? (
        <div className="text-xs text-muted-foreground/70 py-2">{tfr("Aucun document. Ajoutez CV, vidéos, statistiques.")}</div>
      ) : (
        <ul className="space-y-2">
          {visible.map((d) => <DocRow key={d.id} doc={d} playerId={playerId} onChange={onChange} compact />)}
          {docs.length > visible.length && (
            <li className="text-[11px] text-info hover:text-info/80 pt-1 cursor-pointer" onClick={() => document.querySelector('[data-testid=tab-docs]')?.click()}>
              Voir tous les documents ({docs.length}) →
            </li>
          )}
        </ul>
      )}
    </Card>
  );
}

function TagsSection({ player, onUpdated }) {
  const [adding, setAdding] = useState(false);
  const [val, setVal] = useState("");
  const tags = player.tags || [];

  const save = async (newTags) => {
    try {
      await api.patch(`/players/${player.id}`, { tags: newTags });
      onUpdated?.();
    } catch { toast.error(tfr("Erreur")); }
  };
  const add = async () => {
    const t = val.trim();
    if (!t) { setAdding(false); return; }
    if (tags.includes(t)) { setAdding(false); setVal(""); return; }
    await save([...tags, t]);
    setVal(""); setAdding(false);
  };
  const remove = (t) => save(tags.filter((x) => x !== t));

  return (
    <Card
      title="Tags"
      testid="card-tags"
      action={
        <button
          onClick={() => setAdding((v) => !v)}
          className="rounded-full p-1.5 hover:bg-accent dark:hover:bg-secondary dark:bg-secondary/50 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="tags-edit-toggle"
          title={tfr("Ajouter un tag")}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-secondary dark:bg-secondary/50 border border-border dark:border-border text-xs"
            data-testid={`tag-${t}`}
          >
            {t}
            {adding && (
              <button onClick={() => remove(t)} className="text-muted-foreground hover:text-danger" aria-label={tfr("Retirer")}>
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        {adding && (
          <div className="inline-flex items-center gap-1">
            <Input
              value={val}
              onChange={(e) => setVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              placeholder={tfr("Nouveau tag…")}
              className="h-7 text-xs w-32 bg-background/60 border-border dark:border-border"
              autoFocus
              data-testid="tag-input"
            />
            <Button size="icon" onClick={add} className="h-7 w-7 bg-primary text-primary-foreground" data-testid="tag-add">
              <Check className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
        {tags.length === 0 && !adding && (
          <span className="text-xs text-muted-foreground/70">{tfr("Aucun tag. Cliquez ✎ pour en ajouter.")}</span>
        )}
      </div>
    </Card>
  );
}

/* ===== CAREER TAB ===== */
function CareerTab({ player, interactions, onUpdated }) {
  const { tf } = useLang();
  return (
    <div className="space-y-4">
      <Card title={tf("Parcours")} testid="card-career">
        <ul className="relative pl-1 space-y-3">
          <li className="relative pl-6">
            <span className="absolute left-1 top-1 h-2 w-2 rounded-full bg-success" />
            <span className="absolute left-[7px] top-3 bottom-[-12px] w-px bg-border dark:bg-secondary/50" aria-hidden />
            <div className="text-sm font-medium">{player.club || tf("Club actuel non renseigné")}</div>
            <div className="text-[11px] text-muted-foreground">{player.level || "—"} · Actuel</div>
          </li>
          <li className="relative pl-6">
            <span className="absolute left-1 top-1 h-2 w-2 rounded-full bg-muted-foreground/40" />
            <div className="text-sm font-medium">{tf("Ajouté à votre suivi")}</div>
            <div className="text-[11px] text-muted-foreground">{formatDate(player.created_at)}</div>
          </li>
        </ul>
      </Card>
      <SeasonStatsCard player={player} interactions={interactions} onUpdated={onUpdated} />
    </div>
  );
}

function currentSeasonLabel() {
  const d = new Date();
  const y = d.getFullYear();
  // Football season: Aug → Jul
  const start = d.getMonth() >= 7 ? y : y - 1;
  const end = String(start + 1).slice(-2);
  return `${start}/${end}`;
}

function SeasonStatsCard({ player, interactions, onUpdated }) {
  const { tf } = useLang();
  const list = interactions || [];
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const thisMonthCount = list.filter((i) => {
    const t = new Date(i.occurred_at);
    return t >= monthStart;
  }).length;
  const prevMonthCount = list.filter((i) => {
    const t = new Date(i.occurred_at);
    return t >= prevMonthStart && t < monthStart;
  }).length;
  const delta = thisMonthCount - prevMonthCount;

  const fileInputRef = useRef(null);
  const [pending, setPending] = useState(null); // {file, sizeLabel}
  const [uploading, setUploading] = useState(false);

  const ACCEPTED = ".csv,.xlsx,.xls,.pdf";
  const MAX_SIZE = 20 * 1024 * 1024;

  const openPicker = () => {
    fileInputRef.current?.click();
  };

  const handleFile = (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["csv", "xlsx", "xls", "pdf"].includes(ext)) {
      toast.error(tf("Format non supporté. Utilisez CSV, XLSX ou PDF."));
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error(tf("Fichier trop volumineux (max 20 Mo)."));
      return;
    }
    setPending({ file, type: ext.toUpperCase() });
  };

  const upload = async () => {
    if (!pending) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pending.file);
      fd.append("doc_type", "stats");
      fd.append("label", pending.file.name.replace(/\.[^.]+$/, ""));
      await api.post(`/players/${player.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(tf("Statistiques importées dans Documents"));
      setPending(null);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tf("Échec de l'import"));
    } finally {
      setUploading(false);
    }
  };

  const goImport = () => {
    openPicker(); // primary action: directly open file picker
  };

  const goToDocsTab = () => {
    document.querySelector('[data-testid=tab-docs]')?.click();
  };

  const statusColor = STATUS_COLOR_MAP[player.status] || STATUS_COLOR_MAP.default;

  return (
    <>
    <section
      className="rounded-3xl border border-border dark:border-border bg-card dark:bg-surface p-5 animate-fade-in"
      data-testid="card-stats"
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept={ACCEPTED}
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = "";
        }}
        data-testid="stats-file-input"
      />

      {/* HEADER */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="text-[11px] md:text-[13px] uppercase tracking-[0.1em] md:tracking-[0.18em] font-semibold text-muted-foreground/85 min-w-0 truncate">
          {tf("Statistiques saison")} <span className="text-muted-foreground/60 font-medium normal-case tracking-normal ml-0.5">{currentSeasonLabel()}</span>
        </h3>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={goImport}
            className="h-9 gap-1.5 border-border dark:border-border hover:bg-accent dark:hover:bg-secondary dark:bg-secondary/50 text-xs md:text-sm px-2.5 md:px-3"
            data-testid="stats-import-btn"
          >
            <Upload className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{tf("Importer")}</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="h-9 w-9 rounded-md border border-border dark:border-border hover:bg-accent dark:hover:bg-secondary dark:bg-secondary/50 text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center"
                data-testid="stats-menu-btn"
                aria-label={tfr("Plus d'actions")}
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); openPicker(); }} data-testid="stats-menu-import">
                <Upload className="h-3.5 w-3.5 mr-2" /> {tf("Importer un fichier stats")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => goToDocsTab()} data-testid="stats-menu-docs">
                <FolderOpen className="h-3.5 w-3.5 mr-2" /> {tf("Voir tous les documents")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => document.querySelector('[data-testid=tab-activity]')?.click()}>
                <ActivityIcon className="h-3.5 w-3.5 mr-2" /> {tf("Voir l'historique")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* KPI GRID */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4" data-testid="stats-kpi-grid">
        <StatKpi
          icon={Users}
          iconClass="bg-info/15 text-info"
          value={thisMonthCount}
          label="Interactions"
          delta={delta}
          testid="stat-interactions"
        />
        <StatKpi
          icon={ActivityIcon}
          iconClass={statusColor}
          value={player.status || "—"}
          label={tfr("Statut")}
          testid="stat-status"
        />
        <StatKpi
          icon={ShieldCheck}
          iconClass="bg-purple-500/15 text-purple-400"
          value={player.level || "—"}
          label="Niveau"
          testid="stat-level"
        />
      </div>
    </section>

    {/* === IMPORT PREVIEW DIALOG === */}
    <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
      <DialogContent className="max-w-md bg-surface border-border dark:border-border" data-testid="stats-import-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold tracking-tight">{tfr("Importer des statistiques")}</DialogTitle>
          <DialogDescription className="text-[12px] text-muted-foreground">
            Le fichier sera ajouté aux documents du joueur (type "Statistiques").
          </DialogDescription>
        </DialogHeader>
        {pending && (
          <div className="space-y-4">
            <div className="rounded-2xl border border-border dark:border-border bg-surface/50 p-4">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-warning/10 border border-warning/30 text-warning flex items-center justify-center shrink-0">
                  <FileSpreadsheet className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate" data-testid="stats-pending-name">{pending.file.name}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Type : <span className="text-foreground/80 font-medium">{pending.type}</span>
                    {" · "}Taille : <span className="text-foreground/80 tabular-nums">{humanSize(pending.file.size)}</span>
                  </div>
                </div>
                <button
                  onClick={() => setPending(null)}
                  className="rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent dark:hover:bg-secondary dark:bg-secondary/50"
                  aria-label={tfr("Retirer")}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground text-center">
              Formats acceptés : <span className="text-info">CSV, XLSX, PDF</span> · max 20 Mo
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => setPending(null)} className="h-10" data-testid="stats-cancel-btn">
            {tfr("Annuler")}
          </Button>
          <Button
            onClick={upload}
            disabled={uploading}
            className="h-10 gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="stats-analyze-btn"
          >
            {uploading ? "Envoi…" : <>Analyser le fichier <ArrowUpRight className="h-3.5 w-3.5" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

const STATUS_COLOR_MAP = {
  "Repéré": "bg-success/15 text-success",
  "Contacté": "bg-info/15 text-info",
  "En discussion": "bg-warning/15 text-warning",
  "Mandat signé": "bg-success/15 text-success",
  "Placé": "bg-success/15 text-success",
  default: "bg-secondary dark:bg-secondary/50 text-foreground",
};

function StatKpi({ icon: Icon, iconClass, value, label, delta, testid }) {
  const positive = typeof delta === "number" && delta > 0;
  const negative = typeof delta === "number" && delta < 0;
  return (
    <div
      className="rounded-2xl border border-border dark:border-border bg-surface/40 p-4 md:p-5 transition-all duration-200 hover:bg-surface/70 hover:border-foreground/25 hover:shadow-[0_4px_20px_rgba(0,0,0,0.2)]"
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "h-11 w-11 rounded-full flex items-center justify-center shrink-0",
            iconClass
          )}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-2xl md:text-3xl font-semibold tabular-nums tracking-tight leading-none truncate">
            {value}
          </div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground/80 mt-1.5">
            {label}
          </div>
        </div>
      </div>
      {typeof delta === "number" && (
        <div
          className={cn(
            "mt-3 flex items-center gap-1 text-[11px] font-medium",
            positive && "text-success",
            negative && "text-danger",
            !positive && !negative && "text-muted-foreground"
          )}
          data-testid={`${testid}-delta`}
        >
          <ArrowUpRight className={cn("h-3 w-3", negative && "rotate-90")} />
          <span>
            {delta > 0 ? `+${delta}` : delta} vs mois dernier
          </span>
        </div>
      )}
    </div>
  );
}

/* ===== DOCUMENTS TAB ===== */

/**
 * Compute expiration state for a document.
 * Returns one of: 'none' | 'safe' | 'soon' | 'urgent' | 'expired'
 * along with days_left (negative if expired) and a color class set.
 */
function expirationState(expires_at) {
  if (!expires_at) return { state: "none", days: null };
  const exp = new Date(expires_at);
  if (isNaN(exp.getTime())) return { state: "none", days: null };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expDay = new Date(exp);
  expDay.setHours(0, 0, 0, 0);
  const days = Math.round((expDay.getTime() - today.getTime()) / 86400000);
  if (days < 0) return { state: "expired", days };
  if (days < 7) return { state: "urgent", days };
  if (days <= 30) return { state: "soon", days };
  return { state: "safe", days };
}

const EXP_STYLE = {
  none: { wrapper: "text-muted-foreground/70", value: "text-muted-foreground" },
  safe: { wrapper: "text-muted-foreground/80", value: "text-success" },
  soon: { wrapper: "text-muted-foreground/80", value: "text-warning" },
  urgent: { wrapper: "text-muted-foreground/80", value: "text-danger" },
  expired: { wrapper: "text-muted-foreground/80", value: "text-danger" },
};

const QUICK_TYPES = [
  { v: "cv", label: "CV sportif", sub: "PDF", Icon: FileText, color: "text-danger bg-danger/10 border-danger/30" },
  { v: "highlights", label: "Highlights", sub: tfr("Vidéo (MP4)"), Icon: FileVideo, color: "text-info bg-info/10 border-info/30" },
  { v: "stats", label: "Statistiques", sub: "PDF / Excel", Icon: FileSpreadsheet, color: "text-warning bg-warning/10 border-warning/30" },
  { v: "id", label: tfr("Pièce d'identité"), sub: "PDF / JPG", Icon: IdCard, color: "text-info bg-info/10 border-info/30" },
];

const ACCEPTED_FORMATS = ["PDF", "DOC", "DOCX", "MP4", "JPG", "PNG"];

function DocumentsTab({ playerId, docs, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("other");
  const [filterType, setFilterType] = useState("__all__");
  const [pending, setPending] = useState(null); // { files: File[], type: string }

  const onFilesPicked = (files) => {
    if (!files || files.length === 0) return;
    setPending({ files: Array.from(files), type: docType });
  };

  const uploadPending = async ({ expiresAt }) => {
    if (!pending) return;
    setUploading(true);
    try {
      for (const f of pending.files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("doc_type", pending.type);
        fd.append("label", f.name.replace(/\.[^.]+$/, ""));
        if (expiresAt) fd.append("expires_at", expiresAt);
        await api.post(`/players/${playerId}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(pending.files.length > 1 ? `${pending.files.length} ${tfr("documents ajoutés")}` : tfr("Document ajouté"));
      setPending(null);
      onChange?.();
    } catch (e) {
      const qm = quotaMessage(e);
      const d = e?.response?.data?.detail;
      toast.error(qm || (typeof d === "string" ? d : tfr("Échec de l'upload")));
    } finally { setUploading(false); }
  };

  const browse = (typeOverride = null) => {
    if (typeOverride) setDocType(typeOverride);
    setTimeout(() => inputRef.current?.click(), 0);
  };

  const filtered = filterType === "__all__"
    ? docs
    : docs.filter((d) => d.doc_type === filterType);

  return (
    <div className="space-y-4" data-testid="documents-tab">
      <section
        className="rounded-3xl border border-border dark:border-border bg-card dark:bg-surface p-5 space-y-6"
        data-testid="card-documents-full"
      >
        {/* Hidden file input */}
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple
          onChange={(e) => { onFilesPicked(e.target.files); e.target.value = ""; }}
          data-testid="doc-input"
        />

        {/* HEADER */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="text-[11px] uppercase tracking-[0.18em] font-semibold text-foreground/85">
            Documents
          </h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap -mt-2">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger
              className="h-10 flex-1 min-w-[140px] border-border dark:border-border bg-background/60"
              data-testid="doc-filter-type"
            >
              <SelectValue placeholder={tfr("Tous les types")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{tfr("Tous les types")}</SelectItem>
              {Object.entries(DOC_TYPE_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => browse()}
            disabled={uploading}
            className="h-10 px-4 gap-1.5 bg-info text-white hover:bg-info/90 shadow-sm"
            data-testid="doc-add-btn"
          >
            <Plus className="h-4 w-4" /> {uploading ? tfr("Envoi…") : tfr("Ajouter un document")}
          </Button>
        </div>

        {/* EXISTING DOCS LIST (if any & filtered match) */}
        {filtered.length > 0 && (
          <ul className="space-y-2" data-testid="docs-list">
            {filtered.map((d) => (
              <DocRow key={d.id} doc={d} playerId={playerId} onChange={onChange} />
            ))}
          </ul>
        )}

        {/* DROPZONE */}
        <DocumentsDropzone
          empty={docs.length === 0}
          uploading={uploading}
          onBrowse={() => browse()}
          onDrop={(files) => onFilesPicked(files)}
        />

        {/* ACCEPTED FORMATS */}
        <div className="text-center text-[12px] text-muted-foreground">
          Types acceptés :{" "}
          {ACCEPTED_FORMATS.map((f, i) => (
            <React.Fragment key={f}>
              <span className="text-info font-medium">{f}</span>
              {i < ACCEPTED_FORMATS.length - 1 && <span>, </span>}
            </React.Fragment>
          ))}
          {" "}<span className="text-muted-foreground/70">(max. 20 Mo)</span>
        </div>

        {/* QUICK-PICK CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="doc-quick-cards">
          {QUICK_TYPES.map((t) => {
            const Icon = t.Icon;
            return (
              <button
                key={t.v}
                onClick={() => browse(t.v)}
                disabled={uploading}
                className="group rounded-2xl border border-border dark:border-border bg-surface/40 hover:bg-surface/80 hover:border-foreground/25 p-3 md:p-4 flex flex-col items-start text-left transition-all duration-150 disabled:opacity-50"
                data-testid={`doc-quick-${t.v}`}
                title={`${tfr("Ajouter un")} ${t.label}`}
              >
                <div className={cn(
                  "h-10 w-10 rounded-xl border flex items-center justify-center mb-2 transition-transform duration-150 group-hover:scale-105",
                  t.color
                )}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="text-sm font-semibold text-foreground/90 leading-tight">{t.label}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{t.sub}</div>
              </button>
            );
          })}
        </div>

        {/* FOOTER LINK (if filtered hides docs) */}
        {filterType !== "__all__" && docs.length > filtered.length && (
          <div className="text-center pt-2 border-t border-border/70 dark:border-border">
            <button
              onClick={() => setFilterType("__all__")}
              className="text-sm font-medium text-info hover:text-info/80 inline-flex items-center gap-1"
              data-testid="doc-see-all"
            >
              Voir tous les documents ({docs.length}) <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </section>

      {/* === UPLOAD DIALOG WITH EXPIRATION === */}
      <UploadDocumentDialog
        pending={pending}
        onCancel={() => setPending(null)}
        onConfirm={uploadPending}
        uploading={uploading}
      />
    </div>
  );
}

function UploadDocumentDialog({ pending, onCancel, onConfirm, uploading }) {
  const [hasExp, setHasExp] = useState(false);
  const [exp, setExp] = useState("");

  // Reset state when dialog re-opens
  useEffect(() => {
    if (pending) { setHasExp(false); setExp(""); }
  }, [pending, setHasExp, setExp]);

  const open = !!pending;
  const fileCount = pending?.files?.length || 0;
  const firstFile = pending?.files?.[0];
  const meta = pending ? DOC_TYPE_META[pending.type] || DOC_TYPE_META.other : null;
  const Icon = meta?.Icon || FileText;

  const submit = () => {
    onConfirm?.({ expiresAt: hasExp ? exp : null });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel?.()}>
      <DialogContent className="max-w-md bg-surface border-border dark:border-border" data-testid="upload-doc-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold tracking-tight">
            {fileCount > 1 ? `${tfr("Ajouter")} ${fileCount} ${tfr("documents")}` : tfr("Ajouter un document")}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-muted-foreground">
            {tfr("Définissez une date d'expiration si applicable (carte d'identité, contrat, etc.).")}
          </DialogDescription>
        </DialogHeader>
        {firstFile && (
          <div className="space-y-4">
            <div className="rounded-2xl border border-border dark:border-border bg-surface/50 p-4">
              <div className="flex items-center gap-3">
                <div className={cn("h-11 w-11 rounded-xl border flex items-center justify-center shrink-0", meta?.color || "bg-secondary dark:bg-secondary/50 text-foreground")}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate" data-testid="upload-pending-name">{firstFile.name}{fileCount > 1 ? ` + ${fileCount - 1} autre${fileCount > 2 ? "s" : ""}` : ""}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Type : <span className="text-foreground/80 font-medium">{meta?.label}</span>
                    {" · "}<span className="tabular-nums">{humanSize(firstFile.size)}</span>
                  </div>
                </div>
              </div>
            </div>
            {/* Expiration toggle */}
            <div className="rounded-2xl border border-border dark:border-border bg-surface/50 p-4 space-y-3">
              <button
                type="button"
                onClick={() => setHasExp((v) => !v)}
                className="flex items-center gap-3 w-full text-left"
                data-testid="upload-exp-toggle"
              >
                <div className={cn(
                  "relative h-5 w-9 rounded-full transition-colors duration-200 shrink-0",
                  hasExp ? "bg-primary" : "bg-secondary dark:bg-secondary/60"
                )}>
                  <span className={cn(
                    "absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform duration-200",
                    hasExp ? "translate-x-[18px]" : "translate-x-0.5"
                  )} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{tfr("Ajouter une date d'expiration")}</div>
                  <div className="text-[11px] text-muted-foreground">{tfr("Optionnel — utile pour passeports, contrats, certificats.")}</div>
                </div>
              </button>
              {hasExp && (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground/80 mb-1.5 block">
                    Date d'expiration
                  </Label>
                  <Input
                    type="date"
                    value={exp}
                    onChange={(e) => setExp(e.target.value)}
                    className="h-10 bg-background/60 border-border dark:border-border"
                    data-testid="upload-exp-input"
                  />
                </div>
              )}
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onCancel} className="h-10" data-testid="upload-cancel-btn">
            {tfr("Annuler")}
          </Button>
          <Button
            onClick={submit}
            disabled={uploading || (hasExp && !exp)}
            className="h-10 gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="upload-confirm-btn"
          >
            {uploading ? tfr("Envoi…") : <>{tfr("Importer")} <Plus className="h-3.5 w-3.5" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocumentsDropzone({ empty, uploading, onBrowse, onDrop }) {
  const [drag, setDrag] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        onDrop?.(e.dataTransfer.files);
      }}
      className={cn(
        "rounded-2xl border-2 border-dashed transition-all duration-200 flex flex-col items-center justify-center text-center px-4 py-10 md:py-12",
        drag
          ? "border-info bg-info/5"
          : "border-border dark:border-border bg-background/30 hover:border-foreground/25 hover:bg-background/50"
      )}
      data-testid="doc-dropzone"
    >
      <div className={cn(
        "h-14 w-14 rounded-2xl border flex items-center justify-center mb-4 transition-all duration-200",
        drag || uploading ? "bg-info/15 border-info/40 text-info" : "bg-info/10 border-info/30 text-info"
      )}>
        <Upload className={cn("h-5 w-5", uploading && "animate-pulse")} />
      </div>
      <div className="text-base font-semibold text-foreground">
        {empty ? tfr("Aucun document") : tfr("Ajouter un document")}
      </div>
      <div className="text-[12px] text-muted-foreground mt-1.5 max-w-md">
        {empty
          ? tfr("Ajoutez des CV, vidéos, statistiques ou tout autre document lié au joueur.")
          : tfr("Glissez un fichier ici ou cliquez pour parcourir.")}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onBrowse}
        disabled={uploading}
        className="mt-4 h-9 gap-1.5 border-border hover:bg-accent dark:hover:bg-secondary dark:bg-secondary/50 hover:border-foreground/25"
        data-testid="doc-browse-btn"
      >
        <FolderOpen className="h-3.5 w-3.5" /> {tfr("Parcourir les fichiers")}
      </Button>
    </div>
  );
}

function DocRow({ doc, playerId, onChange, compact }) {
  const meta = DOC_TYPE_META[doc.doc_type] || DOC_TYPE_META.other;
  const Icon = meta.Icon;
  const sizeLabel = humanSize(doc.size);

  const download = async () => {
    try {
      const r = await api.get(`/players/${playerId}/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error(tfr("Erreur téléchargement")); }
  };

  const remove = async () => {
    if (!window.confirm(tfr("Supprimer ce document ?"))) return;
    try {
      await api.delete(`/players/${playerId}/documents/${doc.id}`);
      toast.success(tfr("Document supprimé"));
      onChange?.();
    } catch { toast.error(tfr("Erreur")); }
  };

  return (
    <li
      className="flex items-center gap-3 rounded-2xl border border-border dark:border-border bg-surface/50 px-3 py-2.5 hover:bg-surface/80 transition-colors"
      data-testid={`doc-row-${doc.id}`}
    >
      <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center shrink-0", meta.color)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{doc.label}</div>
        <div className="text-[11px] text-muted-foreground truncate">
          {(doc.mime || "").split("/").pop()?.toUpperCase() || "FILE"} · {sizeLabel} · {formatDate(doc.created_at)}
        </div>
      </div>
      {!compact && <DocExpirationBlock expires_at={doc.expires_at} />}
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={download} className="rounded-full p-2 text-muted-foreground hover:text-foreground hover:bg-accent dark:hover:bg-secondary dark:bg-secondary/50" title={tfr("Télécharger")} data-testid={`doc-download-${doc.id}`}>
          <Download className="h-4 w-4" />
        </button>
        {!compact && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="rounded-full p-2 text-muted-foreground hover:text-foreground hover:bg-accent dark:hover:bg-secondary dark:bg-secondary/50" data-testid={`doc-more-${doc.id}`}>
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={download}>
                <Download className="h-3.5 w-3.5 mr-2" /> Télécharger
              </DropdownMenuItem>
              <DocExpirationEditItem doc={doc} playerId={playerId} onChange={onChange} />
              <DropdownMenuItem onClick={remove} className="text-danger focus:text-danger">
                <Trash2 className="h-3.5 w-3.5 mr-2" /> Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </li>
  );
}

function DocExpirationBlock({ expires_at }) {
  const { state, days } = expirationState(expires_at);
  const style = EXP_STYLE[state];

  if (state === "none") {
    return (
      <div className="hidden sm:block w-32 shrink-0 text-right" data-testid="doc-exp-none">
        <div className={cn("text-[11px]", style.value)}>Pas d'expiration</div>
      </div>
    );
  }

  let topLine, bottomLine;
  if (state === "expired") {
    topLine = tfr("Expiré");
    bottomLine = formatDate(expires_at);
  } else {
    topLine = `${days} ${days > 1 ? tfr("jours") : tfr("jour")}`;
    bottomLine = formatDate(expires_at);
  }

  return (
    <div className="hidden sm:block w-32 shrink-0 text-right" data-testid="doc-exp">
      <div className={cn("text-[10px] uppercase tracking-wider", style.wrapper)}>
        {state === "expired" ? "Statut" : "Expire dans"}
      </div>
      <div className={cn("text-sm font-semibold tabular-nums leading-tight mt-0.5", style.value)} data-testid="doc-exp-countdown">
        {topLine}
      </div>
      <div className="text-[10px] text-muted-foreground/70 mt-0.5 truncate">
        {bottomLine}
      </div>
    </div>
  );
}

function DocExpirationEditItem({ doc, playerId, onChange }) {
  const onClick = async () => {
    const cur = doc.expires_at ? doc.expires_at.slice(0, 10) : "";
    const next = window.prompt(
      doc.expires_at
        ? tfr("Modifier la date d'expiration (YYYY-MM-DD) ou laisser vide pour retirer :")
        : tfr("Date d'expiration (YYYY-MM-DD) :"),
      cur
    );
    if (next === null) return;
    const trimmed = next.trim();
    try {
      await api.patch(`/players/${playerId}/documents/${doc.id}`, {
        expires_at: trimmed || null,
      });
      toast.success(trimmed ? tfr("Date d'expiration mise à jour") : tfr("Expiration retirée"));
      onChange?.();
    } catch { toast.error(tfr("Erreur")); }
  };
  return (
    <DropdownMenuItem onClick={onClick} data-testid={`doc-edit-exp-${doc.id}`}>
      <CalIcon className="h-3.5 w-3.5 mr-2" /> {doc.expires_at ? tfr("Modifier l'expiration") : tfr("Ajouter une expiration")}
    </DropdownMenuItem>
  );
}

/* ===== ACTIVITY TAB ===== */
function ActivityTab({ interactions, reminders }) {
  return (
    <div className="space-y-4">
      <Card title={`Interactions (${interactions?.length || 0})`} testid="card-interactions">
        {(!interactions || interactions.length === 0) ? (
          <div className="text-xs text-muted-foreground py-2">{tfr("Aucune interaction enregistrée.")}</div>
        ) : (
          <ul className="space-y-3">
            {interactions.slice(0, 30).map((i) => (
              <li key={i.id} className="flex items-start gap-3 py-1">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-info shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{tfr(i.note) || (DOC_TYPE_META[i.type]?.label) || i.type}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {(i.type || "").toUpperCase()} · {relativeFr(i.occurred_at)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title={`Rappels (${reminders?.length || 0})`} testid="card-reminders">
        {(!reminders || reminders.length === 0) ? (
          <div className="text-xs text-muted-foreground py-2">{tfr("Aucun rappel.")}</div>
        ) : (
          <ul className="space-y-3">
            {reminders.map((r) => (
              <li key={r.id} className="flex items-start gap-3">
                <span className={cn("mt-1 h-1.5 w-1.5 rounded-full shrink-0", r.completed ? "bg-success" : "bg-warning")} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{r.note || tfr("Rappel")}</div>
                  <div className="text-[11px] text-muted-foreground">{formatDate(r.due_at)}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/* ===== STICKY FOOTER ACTIONS ===== */
function FooterActions({ player, onLogged }) {
  const phone = (player.phone || "").trim();
  const email = (player.email || "").trim();

  const log = async (type, note) => {
    try {
      await api.post("/interactions", { player_id: player.id, type, note });
      onLogged?.();
    } catch (error) {
      console.error("[PlayerProfile.FooterActions] log interaction failed:", error);
    }
  };

  const sendMessage = () => {
    if (!phone) { toast.error(tfr("Aucun numéro renseigné")); return; }
    window.location.href = `sms:${phone.replace(/\s/g, "")}`;
    log("message", "SMS depuis fiche");
  };
  const sendEmail = () => {
    if (!email) { toast.error(tfr("Aucun email renseigné")); return; }
    const subject = encodeURIComponent(`${tfr("À propos de")} ${player.name}`);
    const body = encodeURIComponent(`Bonjour,\n\nJe reviens vers vous au sujet de ${player.name}.\n\nCordialement.`);
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    log("email", "Email depuis fiche");
  };
  const sendWhatsApp = () => {
    if (!phone) { toast.error(tfr("Aucun numéro renseigné")); return; }
    const cleaned = phone.replace(/[^\d+]/g, "").replace(/^\+/, "");
    const text = encodeURIComponent(`Bonjour, je vous contacte au sujet de ${player.name}.`);
    window.open(`https://wa.me/${cleaned}?text=${text}`, "_blank", "noopener");
    log("whatsapp", "WhatsApp depuis fiche");
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-30 px-4 md:px-8 py-3 bg-background/90 backdrop-blur-md border-t border-border dark:border-border md:hidden"
      data-testid="profile-footer"
    >
      <div className="max-w-5xl mx-auto grid grid-cols-3 gap-2">
        <FooterButton onClick={sendMessage} icon={MessageSquare} label="Message" testid="footer-message" />
        <FooterButton onClick={sendEmail} icon={Mail} label="Email" testid="footer-email" />
        <FooterButton onClick={sendWhatsApp} icon={Send} label="WhatsApp" testid="footer-whatsapp" primary />
      </div>
    </div>
  );
}

function FooterButton({ onClick, icon: Icon, label, testid, primary }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center justify-center gap-1.5 h-11 rounded-full text-xs font-semibold transition-colors duration-150",
        primary
          ? "bg-primary text-primary-foreground hover:bg-primary/90"
          : "border border-border dark:border-border text-foreground hover:bg-accent dark:hover:bg-secondary dark:bg-secondary/50"
      )}
      data-testid={testid}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

/* ===== Helpers ===== */
function humanSize(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}
